import { useState, useEffect } from 'react'
import { NavLink, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { HiMenuAlt3, HiX, HiChevronDown, HiShoppingCart, HiUser, HiLogout } from 'react-icons/hi'
import { useSite } from '../../context/SiteContext'
import { useMenu } from '../../hooks/useMenu'
import { useCart } from '../../context/CartContext'
import { useAuth } from '../../context/AuthContext'
import Button from '../ui/Button'

// ─── Cartoon mouse SVG logo ───────────────────────────────────────────────────
function MouseLogo({ size = 44 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Ears */}
      <circle cx="13" cy="14" r="10" fill="#FF6BB5" />
      <circle cx="13" cy="14" r="6"  fill="#FFB3D9" />
      <circle cx="35" cy="14" r="10" fill="#FF6BB5" />
      <circle cx="35" cy="14" r="6"  fill="#FFB3D9" />
      {/* Head */}
      <ellipse cx="24" cy="31" rx="17" ry="13" fill="#1A0A2E" />
      {/* Eyes */}
      <circle cx="18" cy="28" r="3"   fill="white" />
      <circle cx="30" cy="28" r="3"   fill="white" />
      <circle cx="19" cy="29" r="1.5" fill="#1A0A2E" />
      <circle cx="31" cy="29" r="1.5" fill="#1A0A2E" />
      {/* Eye sparkle */}
      <circle cx="19.8" cy="27.6" r="0.7" fill="white" />
      <circle cx="31.8" cy="27.6" r="0.7" fill="white" />
      {/* Nose */}
      <ellipse cx="24" cy="33.5" rx="2.5" ry="2" fill="#FF6BB5" />
      {/* Whiskers */}
      <line x1="26.5" y1="33" x2="33" y2="31" stroke="#6B5B7A" strokeWidth="0.8" strokeLinecap="round" />
      <line x1="26.5" y1="34" x2="33" y2="34" stroke="#6B5B7A" strokeWidth="0.8" strokeLinecap="round" />
      <line x1="21.5" y1="33" x2="15" y2="31" stroke="#6B5B7A" strokeWidth="0.8" strokeLinecap="round" />
      <line x1="21.5" y1="34" x2="15" y2="34" stroke="#6B5B7A" strokeWidth="0.8" strokeLinecap="round" />
      {/* Bow tie */}
      <polygon points="19,42 24,39 29,42 24,45" fill="#FFC300" />
      <circle cx="24" cy="42" r="1.8" fill="#E6A800" />
    </svg>
  )
}

// ─── Mobile menu animation ────────────────────────────────────────────────────
const menuVariants = {
  hidden: { opacity: 0, y: -16, scale: 0.98 },
  show:   { opacity: 1, y: 0,   scale: 1,    transition: { duration: 0.28, ease: 'easeOut' } },
  exit:   { opacity: 0, y: -16, scale: 0.98, transition: { duration: 0.2 } },
}

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  show: (i) => ({ opacity: 1, x: 0, transition: { delay: i * 0.07, duration: 0.3 } }),
}

// ─── Desktop nav item (with optional dropdown) ────────────────────────────────
function DesktopNavItem({ item, linkBase, activeClass, inactiveClass }) {
  const [open, setOpen] = useState(false)
  const hasChildren = item.children?.length > 0

  return (
    <div
      className="relative"
      onMouseEnter={() => hasChildren && setOpen(true)}
      onMouseLeave={() => hasChildren && setOpen(false)}
    >
      <NavLink
        to={item.path}
        end={item.path === '/'}
        className={({ isActive }) =>
          [linkBase, isActive ? activeClass : inactiveClass, hasChildren ? 'flex items-center gap-1' : ''].join(' ')
        }
      >
        {item.label}
        {hasChildren && (
          <HiChevronDown
            size={13}
            className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        )}
      </NavLink>

      {hasChildren && (
        <AnimatePresence>
          {open && (
            <motion.ul
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.18 }}
              className="absolute top-full left-0 mt-2 min-w-[160px] bg-white rounded-xl shadow-lg border border-ink/5 py-1.5 z-50"
            >
              {item.children.map(child => (
                <li key={child.id}>
                  <NavLink
                    to={child.path}
                    end={child.path === '/'}
                    className={({ isActive }) =>
                      [
                        'block px-4 py-2 text-sm font-body font-semibold whitespace-nowrap transition-colors duration-150',
                        isActive ? 'text-primary bg-primary/5' : 'text-ink/70 hover:text-primary hover:bg-primary/5',
                      ].join(' ')
                    }
                  >
                    {child.label}
                  </NavLink>
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      )}
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function Header() {
  const [scrolled,       setScrolled]       = useState(false)
  const [menuOpen,       setMenuOpen]       = useState(false)
  const [openMobileItem, setOpenMobileItem] = useState(null)
  const [userMenuOpen,   setUserMenuOpen]   = useState(false)
  const { siteOptions } = useSite()
  const { items: navLinks } = useMenu()
  const { itemCount, setIsOpen: openCart } = useCart()
  const { user, isLoggedIn, logout, openAuthModal } = useAuth()

  // Scroll detection
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close menu on desktop resize
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setMenuOpen(false) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  const activeClass   = 'text-primary font-bold after:w-full'
  const inactiveClass = 'text-ink/70 hover:text-primary after:w-0 hover:after:w-full'
  const linkBase      = [
    'font-body font-semibold text-sm tracking-wide relative',
    'after:absolute after:bottom-[-3px] after:left-0',
    'after:h-[2px] after:bg-primary after:rounded-full',
    'after:transition-[width] after:duration-300',
    'transition-colors duration-200',
  ].join(' ')

  return (
    <>
      {/* ── Fixed header bar ─────────────────────────────────────────────── */}
      <motion.header
        initial={false}
        animate={{
          backgroundColor: scrolled ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0)',
          backdropFilter:   scrolled ? 'blur(16px)' : 'blur(0px)',
          boxShadow:        scrolled ? '0 2px 24px rgba(26,10,46,0.09)' : 'none',
        }}
        transition={{ duration: 0.35 }}
        className="fixed top-0 inset-x-0 z-50"
        dir="ltr" // RTL-ready: change to "rtl" for Arabic
      >
        <div className="container-custom flex items-center justify-between h-20">

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 shrink-0 group"
            onClick={() => setMenuOpen(false)}
          >
            <motion.div
              whileHover={{ rotate: [-5, 5, -5, 0], scale: 1.05 }}
              transition={{ duration: 0.5 }}
            >
              <MouseLogo size={44} />
            </motion.div>
            <span className="font-display text-xl leading-none text-ink">
              Farafeero
              <span className="text-primary"> World</span>
            </span>
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center gap-7" aria-label="Main navigation">
            {navLinks.map((item) => (
              <DesktopNavItem
                key={item.id}
                item={item}
                linkBase={linkBase}
                activeClass={activeClass}
                inactiveClass={inactiveClass}
              />
            ))}
          </nav>

          {/* Desktop right actions */}
          <div className="hidden md:flex items-center gap-3">
            {siteOptions?.contact?.phone && (
              <a
                href={`tel:${siteOptions.contact.phone}`}
                className="font-body text-sm text-ink-muted hover:text-primary transition-colors duration-200"
              >
                📞 {siteOptions.contact.phone}
              </a>
            )}

            {/* Cart icon */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => openCart(true)}
              className="relative w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors"
              aria-label="Open cart"
            >
              <HiShoppingCart size={20} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </motion.button>

            {/* Auth */}
            {isLoggedIn ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  <HiUser size={16} />
                  <span className="font-body text-sm font-semibold">{user.first_name || 'Account'}</span>
                </button>
                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-ink/5 py-1.5 z-50"
                    >
                      <button
                        onClick={() => { logout(); setUserMenuOpen(false) }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-body text-ink/70 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <HiLogout size={15} /> Sign Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <button
                onClick={() => openAuthModal('login')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-primary/30 text-primary font-body text-sm font-semibold hover:bg-primary/10 transition-colors"
              >
                <HiUser size={15} /> Sign In
              </button>
            )}

            <Button to="/contact" size="sm" variant="primary">
              Book Now
            </Button>
          </div>

          {/* Mobile: cart + hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => openCart(true)}
              className="relative w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"
              aria-label="Open cart"
            >
              <HiShoppingCart size={20} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-white text-[9px] font-bold flex items-center justify-center">
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.9 }}
              className={[
                'w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-200',
                menuOpen ? 'bg-primary text-white' : 'bg-primary/10 text-primary',
              ].join(' ')}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <HiX size={22} /> : <HiMenuAlt3 size={22} />}
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* ── Full-screen mobile menu ───────────────────────────────────────── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-menu"
            variants={menuVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            className="fixed inset-0 z-40 flex flex-col pt-20 md:hidden"
            style={{ background: 'linear-gradient(135deg, #FF6BB5 0%, #7C3AFF 100%)' }}
          >
            {/* Decorative orbs */}
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none" />

            {/* Nav links */}
            <nav
              className="relative z-10 flex-1 flex flex-col items-center justify-center gap-2 px-8"
              aria-label="Mobile navigation"
            >
              {navLinks.map((item, i) => {
                const hasChildren = item.children?.length > 0
                const isExpanded  = openMobileItem === item.id

                return (
                  <motion.div key={item.id} custom={i} variants={itemVariants} initial="hidden" animate="show" className="w-full max-w-xs">
                    {hasChildren ? (
                      <>
                        <button
                          onClick={() => setOpenMobileItem(isExpanded ? null : item.id)}
                          className="w-full flex items-center justify-center gap-2 py-4 text-2xl font-display transition-all duration-200 rounded-2xl text-white/80 hover:text-white hover:bg-white/10"
                        >
                          {item.label}
                          <HiChevronDown
                            size={20}
                            className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.22 }}
                              className="overflow-hidden"
                            >
                              {item.children.map(child => (
                                <NavLink
                                  key={child.id}
                                  to={child.path}
                                  end={child.path === '/'}
                                  onClick={() => { setMenuOpen(false); setOpenMobileItem(null) }}
                                  className={({ isActive }) =>
                                    [
                                      'block text-center py-2.5 text-lg font-display transition-all duration-200 rounded-xl mx-2',
                                      isActive
                                        ? 'text-white bg-white/20 font-bold'
                                        : 'text-white/70 hover:text-white hover:bg-white/10',
                                    ].join(' ')
                                  }
                                >
                                  {child.label}
                                </NavLink>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <NavLink
                        to={item.path}
                        end={item.path === '/'}
                        onClick={() => setMenuOpen(false)}
                        className={({ isActive }) =>
                          [
                            'block text-center py-4 text-2xl font-display transition-all duration-200 rounded-2xl',
                            isActive
                              ? 'text-white bg-white/20 font-bold'
                              : 'text-white/80 hover:text-white hover:bg-white/10',
                          ].join(' ')
                        }
                      >
                        {item.label}
                      </NavLink>
                    )}
                  </motion.div>
                )
              })}

              {/* Book Now CTA */}
              <motion.div
                custom={navLinks.length}
                variants={itemVariants}
                initial="hidden"
                animate="show"
                className="w-full max-w-xs mt-6"
              >
                <Link
                  to="/contact"
                  onClick={() => setMenuOpen(false)}
                  className="block text-center py-4 bg-white text-primary font-display text-xl rounded-full hover:bg-primary-100 transition-colors duration-200"
                >
                  Book a Party 🎂
                </Link>
              </motion.div>

              {/* Auth CTA */}
              <motion.div
                custom={navLinks.length + 1}
                variants={itemVariants}
                initial="hidden"
                animate="show"
                className="w-full max-w-xs mt-3"
              >
                {isLoggedIn ? (
                  <button
                    onClick={() => { logout(); setMenuOpen(false) }}
                    className="w-full flex items-center justify-center gap-2 py-3 text-white/70 font-body text-base hover:text-white transition-colors"
                  >
                    <HiLogout size={18} /> Sign Out ({user.first_name})
                  </button>
                ) : (
                  <button
                    onClick={() => { openAuthModal('login'); setMenuOpen(false) }}
                    className="w-full flex items-center justify-center gap-2 py-3 text-white/70 font-body text-base hover:text-white transition-colors"
                  >
                    <HiUser size={18} /> Sign In
                  </button>
                )}
              </motion.div>
            </nav>

            {/* Phone number at bottom */}
            {siteOptions?.contact?.phone && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="relative z-10 pb-10 text-center"
              >
                <a
                  href={`tel:${siteOptions.contact.phone}`}
                  className="text-white/70 font-body text-sm hover:text-white transition-colors"
                >
                  📞 {siteOptions.contact.phone}
                </a>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
